/**
 * Adds the dynamic behavior to the carousel (e.g., scrolling on click).
 */
class ProductCarouselSlider extends HTMLElement {
    instantScrollClass = 'ww-product-carousel__slides--instant-scroll';
    slides;
    allSlides;
    slidesContainer;
    prev;
    next;
    scrollTimeout;
    intersectionObserver;
    visibility = {
        before: {},
        original: {},
        after: {},
    };
    slideTypes = Object.keys(this.visibility);

    /**
     * This is called once the element has been connected to the DOM tree.
     * This is treated as an initialization function here.
     */
    connectedCallback() {
        this.storeElements();
        this.cloneSlides();
        this.observeSlides();
        this.configurePagers();
        this.setInitialPosition();
        this.listenForScrollCompletion();
    }

    /**
     * Ensures the original slides are the ones initially shown (rather than
     * the "before" slides).
     */
    setInitialPosition() {
        const firstOriginalSlide = this.slidesContainer.querySelector('[data-original="0"]');
        this.scrollToSlide(firstOriginalSlide, true);
    }

    /**
     * Scrolls the carousel such that the specified slide will be the leftmost
     * visible slide.
     * @param slide The slide to scroll to.
     * @param instantly Should this be an instant or a smooth scroll?
     */
    scrollToSlide(slide, instantly) {
        if (slide) {
            // Temporarily disable smooth scrolling?
            if (instantly) {
                this.slidesContainer.classList.add(this.instantScrollClass);
            }

            // Scroll to the slide.
            this.slidesContainer.scrollLeft = slide.offsetLeft;

            // Re-enable smooth scrolling after the initial scroll?
            if (instantly) {
                setTimeout(() => {
                    this.slidesContainer.classList.remove(this.instantScrollClass);
                }, 10);
            }
        }
    }

    /**
     * Configure the event listeners for the pagers.
     */
    configurePagers() {
        this.next.addEventListener('click', this.handleNextClick.bind(this));
        this.prev.addEventListener('click', this.handlePrevClick.bind(this));
    }

    /**
     * Handle clicks of the "Next" pager.
     */
    handleNextClick() {
        const slide = this.getCurrentlyVisibleSlide().nextElementSibling;
        this.scrollToSlide(slide, false);
    }

    /**
     * Handle clicks of the "Previous" pager.
     */
    handlePrevClick() {
        const slide = this.getCurrentlyVisibleSlide().previousElementSibling;
        this.scrollToSlide(slide, false);
    }

    /**
     * Returns the currently visible slide (the one shown on the left of
     * the carousel).
     * @returns {Element} The slide.
     */
    getCurrentlyVisibleSlide() {
        let foundSlide;
        for (const slideType of this.slideTypes) {
            for (const key in this.visibility[slideType]) {
                if (this.visibility[slideType][key]) {
                    const selector = `[data-${slideType}="${key}"]`;
                    return this.querySelector(selector);
                }
            }
        }
        return this.querySelector('[data-original="0"]');
    }

    /**
     * Does housekeeping in the idle periods between scrolling (e.g.,
     * resets the original slides when scrolled too far).
     */
    listenForScrollCompletion() {
        const onScroll = () => {
            if (this.scrollTimeout) {
                clearTimeout(this.scrollTimeout);
            }

            this.scrollTimeout = setTimeout(() => {
                requestAnimationFrame(() => {
                    this.resetToOriginalSlides();
                });
            }, 100);
        };

        this.slidesContainer.addEventListener('scroll', onScroll);
    }

    /**
     * When the user has scrolled too far, reset the carousel so one of the original
     * slides is in the leftmost position.
     */
    resetToOriginalSlides() {
        const slide = this.getCurrentlyVisibleSlide();
        const slideType = this.getSlideType(slide);
        const slideIndex = parseInt(slide.dataset[slideType], 10);
        if (slideType !== 'original') {
            const selector = `[data-original="${slideIndex}"]`;
            const originalSlide = this.querySelector(selector);
            this.scrollToSlide(originalSlide, true);
        }
    }

    /**
     * Gets the type of the specified slide (i.e., before, original, after).
     * @param slide The slide.
     * @returns {string} The slide type.
     */
    getSlideType(slide) {
        let type;
        this.slideTypes.forEach(x => {
            if (slide.dataset.hasOwnProperty(x)) {
                type = x;
            }
        });
        return type || 'original';
    }

    /**
     * Observes when the slides become visible/invisible.
     */
    observeSlides() {
        // Create intersection observer.
        this.intersectionObserver = new IntersectionObserver((entries) => {

            // Store the changes in visibility to a single object that is aware of
            // the current visibility of each slide.
            entries.forEach((entry) => {
                const target = entry.target;
                const dataset = target.dataset;
                const index = dataset.before || dataset.original || dataset.after;
                const type = this.getSlideType(target);
                this.visibility[type][index] = entry.isIntersecting;
            });
        }, {
            // Slides must be 90% visible to be considered visible. Edge
            // doesn't like when 100% is used.
            threshold: 0.9,
            // Set visibility with respect to the widget itself (otherwise,
            // scrolling the page could cause visibility to change).
            root: this,
        });

        // Observe changes to any of the slides (whether they are original slides
        // or cloned slides).
        this.allSlides.forEach((slide) => {
            this.intersectionObserver.observe(slide);
        });
    }

    /**
     * Clones the slides to facilitate the circular nature of the carousel.
     */
    cloneSlides() {
        const firstSlide = this.slides[0];

        // Clone and insert the "before" slides.
        this.slides.forEach((slide, index) => {
            const prevClone = slide.cloneNode(true);
            prevClone.setAttribute('data-before', index);
            // Disable tabbing to the cloned slides.
            prevClone.setAttribute('tabindex', '-1');
            this.slidesContainer.insertBefore(prevClone, firstSlide);
        });

        // Clone and insert the "after" slides, and set the "original" attribute.
        this.slides.forEach((slide, index) => {
            const nextClone = slide.cloneNode(true);
            nextClone.setAttribute('data-after', index);
            // Disable tabbing to the cloned slides.
            nextClone.setAttribute('tabindex', '-1');
            this.slidesContainer.appendChild(nextClone);
            slide.setAttribute('data-original', index);
        });

        // Store all slides (including the clones).
        this.allSlides = this.querySelectorAll('[data-slide]');
    }

    /**
     * Query the carousel for the elements within it that will need to be
     * referred to within the JavaScript.
     */
    storeElements() {
        this.slides = this.querySelectorAll('[data-slide]');
        this.slidesContainer = this.querySelector('[data-slides-container]');
        this.prev = this.querySelector('[data-pager-prev]');
        this.next = this.querySelector('[data-pager-next]');
    }
}

// Define the custom element for the carousel.
window.customElements.define('product-carousel-slider', ProductCarouselSlider);